import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ScrollText, ChevronLeft, ChevronRight } from 'lucide-react';

const actionColors = {
  create: 'bg-emerald-50 text-emerald-700',
  update: 'bg-blue-50 text-blue-700',
  delete: 'bg-rose-50 text-rose-700',
  request_edit: 'bg-amber-50 text-amber-700',
  approve_edit: 'bg-emerald-50 text-emerald-700',
  reject_edit: 'bg-rose-50 text-rose-700',
};

export default function AuditLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ resource_type: '', page: 1 });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await auditAPI.list(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-20" data-testid="audit-no-access">
        <p className="text-slate-500 text-lg">Admin access required to view audit logs.</p>
      </div>
    );
  }

  return (
    <div data-testid="audit-log-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Audit Logs</h1>
          <p className="text-sm text-slate-500 font-body mt-1">{total} actions tracked</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border border-slate-200 rounded-md p-4">
        <select value={filters.resource_type} onChange={(e) => setFilters(f => ({ ...f, resource_type: e.target.value, page: 1 }))}
          className="form-input w-48" data-testid="audit-resource-filter">
          <option value="">All Resources</option>
          <option value="receipt">Receipts</option>
          <option value="invoice">Invoices</option>
          <option value="employee">Employees</option>
          <option value="ledger">Ledger</option>
          <option value="vehicle">Vehicles</option>
          <option value="driver">Drivers</option>
          <option value="branch">Branches</option>
          <option value="bill_book">Bill Books</option>
          <option value="bill_book_request">Bill Book Requests</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="audit-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Resource ID</th>
                  <th>Details</th>
                  <th>IP / Device</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.log_id} data-testid={`audit-row-${log.log_id}`}>
                    <td className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="text-sm">{log.user_name}</td>
                    <td>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${actionColors[log.action] || 'bg-slate-100 text-slate-700'}`}>
                        {log.action?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="capitalize text-sm">{log.resource_type}</td>
                    <td className="font-mono text-xs text-slate-500 max-w-[120px] truncate">{log.resource_id}</td>
                    <td className="text-xs text-slate-500 max-w-[200px] truncate">
                      {log.details && Object.keys(log.details).length > 0
                        ? Object.entries(log.details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ')
                        : '-'}
                    </td>
                    <td className="text-[10px] text-slate-400 max-w-[160px]">
                      {log.ip ? <span className="font-mono block">{log.ip}</span> : null}
                      {log.device ? <span className="block truncate" title={log.device}>{log.device.split(') ')[0].split(' (')[0]}</span> : (!log.ip ? '-' : null)}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-12">
                    <ScrollText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No audit logs found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">Page {filters.page} of {pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))} disabled={filters.page <= 1}
                className="btn-secondary px-3 py-1.5 text-xs" data-testid="audit-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setFilters(f => ({ ...f, page: Math.min(pages, f.page + 1) }))} disabled={filters.page >= pages}
                className="btn-secondary px-3 py-1.5 text-xs" data-testid="audit-next-page">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
